import { FormControlLabel, Radio, RadioGroup } from "@mui/material";

import { type BooleanComparator, isValueless } from "../FilterSchema";

interface BooleanValueInputProps {
  comparator: BooleanComparator;
  value: boolean[];
  onChange: (next: boolean[]) => void;
}

/**
 * Value input for boolean-typed filters.
 *
 * Operator-aware:
 *   - EXISTS / NOT_EXISTS render no input; validity short-circuits to true.
 *   - EQUALS renders a True / False radio pair. The user must pick one to
 *     enable Add filter (no implicit default — boolean fields can be
 *     meaningfully nullable, and presupposing a default would silently
 *     filter for one truth value).
 *
 * Output is `[true]` or `[false]`. Multi-value boolean filters don't make
 * sense on a strictly two-state field, so this input is single-value-only.
 */
export function BooleanValueInput({
  comparator,
  value,
  onChange,
}: BooleanValueInputProps) {
  const valueless = isValueless(comparator);

  if (valueless) {
    return null;
  }

  // RadioGroup needs a string value; "" maps to no selection.
  const current = value.length === 1 ? String(value[0]) : "";

  return (
    <RadioGroup
      row
      value={current}
      onChange={(event) => {
        onChange([event.target.value === "true"]);
      }}
    >
      <FormControlLabel
        value="true"
        control={<Radio size="small" />}
        label="True"
      />
      <FormControlLabel
        value="false"
        control={<Radio size="small" />}
        label="False"
      />
    </RadioGroup>
  );
}
